import PrintablePass, { PrintablePassSnapshot } from "../../components/PrintablePass";
import RequirementsComponent from "../../components/RequirementsComponent";
import React, { useContext, useState } from "react";
import MobileModuleHeader from "../../components/MobileModuleHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels, selectLanguage } from "../../slices/language";
import { Button, CircularProgress, Divider, Alert } from "@mui/material";
import IconifyIcon from "../../components/IconifyIcon";
import { GateData } from "../../api/models/gate-model";
import { AccessGroupCurrent } from "../Logs/Logs";
import { Context } from "../../context/ContextProvider/ContextProvider";
import api, {
  CreateAccessPayload,
  CreateAccessKeyRequest,
  CreateAccessTransactionPhotoPayload,
  CreateAuthorizationPassPayload,
  CreateTransactionPayload,
  SendAuthorizationRequest,
} from "../../api";
import { showError } from "../../utils/helpers";
import { selectHasUserPropertySetting, selectUser } from "../../slices/user";
import dayjs from "dayjs";
import { IDetectedBarcode, Scanner } from "@yudiel/react-qr-scanner";
import {
  AccessKeyCreateResponse,
  NormalAccessKeyData,
} from "../../api/models/access-key-model";
import { AccessData } from "../../api/models/access-model";
import PhotoButton from "../../components/PhotoButton";
import ImageModal from "../../components/Modals/ImageModal";
import ManualEntranceLog from "../../components/ManualEntranceLog";
import { RequirementTypesData } from "../../api/models/requirement-types";
import ManualExitLog from "../../components/ManualExitLog";
import AuthorizationRequestSendedModal from "../../components/Modals/AuthorizationRequestSendedModal";
import { AccessGroupsData } from "../../api/models/access-groups";
import {
  getAccessTransactionPhotoUrl,
  toAbsoluteMediaUrl,
} from "../../utils/getAccessTransactionPhotoUrl";
import "../../components/RegisterGuardLog/register-guard-log.css";
import { sortGatesEntranceFirst } from "../../utils/sortGatesEntranceFirst";
import {
  ManualRegistrationScheduleStatus,
  resolveManualRegistrationConfig,
  resolveRegistrationConfig,
} from "../../utils/resolveManualRegistrationConfig";

interface UnitInfo {
  key: string;
  label: string;
  id: number;
}
interface UserInfo {
  unit: UnitInfo;
  documentId: string;
  key: string;
  label: string;
  id: number;
}
interface GateInfo {
  id: number;
  name: string;
  type: string;
}

const MobLogs = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectLanguage);
  const hasRegisterAuthorization = useAppSelector(
    selectHasUserPropertySetting("security_guard_transactions"),
  );
  const hasUserAccessGroupsPerms = useAppSelector(
    selectHasUserPropertySetting("user_access_groups"),
  );

  const currentLanguage = language.startsWith("es") ? "es" : "en";
  const constraints: MediaTrackConstraints = {
    aspectRatio: 1,
    facingMode: { ideal: "environment" },
  };

  const propertyId = user?.property?.id;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [registerType, setRegisterType] = React.useState<"manual" | "qr">(
    "manual",
  );
  const [gates, setGates] = React.useState<GateData[] | null>(null);
  const visibleGates = gates?.filter(
    (gate) =>
      registerType !== "manual" ||
      hasRegisterAuthorization ||
      gate.type === "entrance",
  );
  const [step, setStep] = React.useState<"step_1" | "step_2" | "step_3">(
    "step_1",
  );
  const [selectedGate, setSelectedGate] = React.useState<GateInfo | null>(null);
  const [accessGroups, setAccessGroups] = React.useState<
    AccessGroupsData[] | null
  >(null);
  const [selectedRule, setSelectedRule] =
    React.useState<AccessGroupCurrent | null>(null);
  const [activeRequirements, setActiveRequirements] = React.useState<
    RequirementTypesData[]
  >([]);
  const [scheduleStatus, setScheduleStatus] =
    React.useState<ManualRegistrationScheduleStatus>("allowed");
  const [qrScheduleStatus, setQrScheduleStatus] =
    React.useState<ManualRegistrationScheduleStatus>("allowed");
  const [qrResolvedRule, setQrResolvedRule] =
    React.useState<AccessGroupCurrent | null>(null);
  const [qrActiveRequirements, setQrActiveRequirements] = React.useState<
    RequirementTypesData[]
  >([]);
  const [unitsArray, setUnitsArray] = React.useState<UnitInfo[]>([]);
  const [usersArray, setUsersArray] = React.useState<UserInfo[]>([]);
  const [message, setMessage] = React.useState<string>("");
  const [validQr, setValidQr] = React.useState<boolean>(false);
  const [isEntrance, setIsEntrance] = React.useState<boolean>(false);
  const [currentAccessKey, setCurrentAccessKey] =
    React.useState<NormalAccessKeyData | null>(null);
  const [qrStatusValue, setQrStatusValue] = React.useState<{
    message: {
      en: string;
      es: string;
    };
    type: "entrance" | "exit" | "try";
  } | null>(null);
  const [visitorSearch, setVisitorSearch] = React.useState<string>("");
  const [selectedAccessToExit, setSelectedAccessToExit] =
    React.useState<AccessData | null>(null);
  const [selectedPhoto, setSelectedPhoto] = React.useState<{
    type: string;
    url: string;
  } | null>(null);
  const [showPictureModal, setShowPictureModal] =
    React.useState<boolean>(false);
  const [accessesToExit, setAccessesToExit] = React.useState<
    AccessData[] | null
  >(null);
  const [accessesLoading, setAccessesLoading] = React.useState<boolean>(false);
  const [visitorName, setVisitorName] = React.useState<string>("");
  const [unit, setUnit] = React.useState<UnitInfo | null>(null);
  const [unitInputValue, setUnitInputValue] = React.useState<string>("");
  const [selectedUser, setSelectedUser] = React.useState<UserInfo | null>(null);
  const [userInputValue, setUserInputValue] = React.useState<string>("");
  const [motive, setMotive] = React.useState<string>("");
  const [idFile, setIdFile] = React.useState<File | null>(null);
  const [plateFile, setPlateFile] = React.useState<File | null>(null);
  const [portraitFile, setPortraitFile] = React.useState<File | null>(null);
  const [equipmentFile, setEquipmentFile] = React.useState<File | null>(null);
  const [countryCode, setCountryCode] = React.useState<"+52" | "+1">("+52");
  const [visitorPhone, setVisitorPhone] = React.useState<string>("");
  const [requirementsValues, setRequirementsValues] = React.useState<any[]>([]);
  const [sendSuccessModal, setShowSendSuccessModal] =
    React.useState<boolean>(false);
  const [userQrId, setUserQrId] = React.useState<number | null>(null);
  const [userQrUnit, setUserQrUnit] = React.useState<number | null>(null);

  const { selectedAccessPointGuard } = useContext(Context);
  const debouncedVisitor = useDebounce(visitorSearch, 300);

  const photos: { type: string; url: string }[] = React.useMemo(() => {
    return (
      selectedAccessToExit?.entrance_transaction?.access_transaction_photos?.map(
        (item) => {
          return {
            type: item?.type, //photo id | plates | visitor | equipment
            url: getAccessTransactionPhotoUrl(item),
          };
        },
      ) || []
    );
  }, [
    selectedAccessToExit?.entrance_transaction
      ?.access_transaction_photos,
  ]);

  const defaultUnitsProps = {
    options: unitsArray ? unitsArray : [],
    getOptionLabel: (option: UnitInfo) => option.label,
  };

  const defaultUsersProps = {
    options: unit ? usersArray.filter((item) => item.unit.id === unit.id) : usersArray,
    getOptionLabel: (option: UserInfo) => option.label,
  };

  const [printablePassOpen, setPrintablePassOpen] = React.useState(false);
  const [printablePassData, setPrintablePassData] = React.useState<PrintablePassSnapshot | null>(null);
  const qrRequirementTypes = validQr && qrScheduleStatus === "allowed" ? qrActiveRequirements : [];
  const finishPrintablePassFlow = () => {
    setPrintablePassOpen(false);
    setPrintablePassData(null);
    setStep("step_1");
    defaultManual();
  };
  const accessKeyPhotos =
    validQr && qrScheduleStatus === "allowed"
      ? (qrResolvedRule?.photographs ?? [])
      : [];
  const manualPhotos = selectedRule?.photographs ?? [];

  type PhotoKey = "id" | "plates" | "visitor" | "other";

  const fileMap: Record<PhotoKey, any> = {
    id: idFile,
    plates: plateFile,
    visitor: portraitFile,
    other: equipmentFile,
  };

  const qrScheduleAllowed = qrScheduleStatus === "allowed";

  const qrScheduleWarningMessage =
    qrScheduleStatus === "exit_not_allowed"
      ? labels.manual_exit_not_allowed_schedule
      : qrScheduleStatus === "entrance_not_allowed"
        ? labels.manual_entrance_not_allowed_schedule
        : "";

  const qrRequiredRequirementsFilled = React.useMemo(() => {
    const reqs = qrRequirementTypes;
    const requiredIndices = reqs
      .map((req, index) => (req?.required ? index : -1))
      .filter((i) => i >= 0);
    if (requiredIndices.length === 0) return true;
    return requiredIndices.every((index) => {
      const val = requirementsValues[index]?.data?.value;
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    });
  }, [qrRequirementTypes, requirementsValues]);

  const disabledQrEntranceLog =
    !qrScheduleAllowed ||
    accessKeyPhotos?.some((key) => !fileMap[key as PhotoKey]) ||
    (qrRequirementTypes.length > 0 && !qrRequiredRequirementsFilled);

  const disabledQrExitLog = !qrScheduleAllowed;

  const requiredRequirementsFilled = React.useMemo(() => {
    const reqs = activeRequirements;
    const requiredIndices = reqs
      .map((req, index) => (req?.required ? index : -1))
      .filter((i) => i >= 0);
    if (requiredIndices.length === 0) return true;
    return requiredIndices.every((index) => {
      const val = requirementsValues[index]?.data?.value;
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    });
  }, [activeRequirements, requirementsValues]);

  const disabledManualLog = hasRegisterAuthorization
    ? manualPhotos?.some(
        (key) => !fileMap[key as PhotoKey] || !selectedUser || !unit,
      ) || !requiredRequirementsFilled
    : !visitorName || !visitorPhone || !selectedUser?.documentId || !unit || !motive;

  const ruleForPrintablePass = React.useMemo((): AccessGroupCurrent | null => {
    if (registerType === "qr") return qrResolvedRule;
    return selectedRule;
  }, [registerType, qrResolvedRule, selectedRule]);

  const selectedAccessPointId =
    selectedAccessPointGuard && selectedAccessPointGuard !== "0"
      ? Number(selectedAccessPointGuard)
      : null;

  const applyManualRegistrationConfig = React.useCallback(
    (gateType: "entrance" | "exit") => {
      const resolved = resolveManualRegistrationConfig(
        accessGroups,
        selectedAccessPointId,
        gateType,
      );
      setScheduleStatus(resolved.status);
      setSelectedRule(resolved.rule);
      setActiveRequirements(resolved.requirements);
      setRequirementsValues([]);
    },
    [accessGroups, selectedAccessPointId],
  );

  const manualScheduleAllowed = scheduleStatus === "allowed";

  const scheduleWarningMessage =
    scheduleStatus === "exit_not_allowed"
      ? labels.manual_exit_not_allowed_schedule
      : scheduleStatus === "entrance_not_allowed"
        ? labels.manual_entrance_not_allowed_schedule
        : "";

  const showLogButton =
    registerType === "manual"
      ? step === "step_2" && manualScheduleAllowed
        ? true
        : false
      : step === "step_3"
        ? true
        : false;

  React.useEffect(() => {
    setStep("step_1");
    setScheduleStatus("allowed");
    setSelectedRule(null);
    setActiveRequirements([]);
  }, [selectedAccessPointGuard, registerType]);

  React.useEffect(() => {
    if (
      registerType === "manual" &&
      step === "step_2" &&
      selectedGate?.type &&
      (selectedGate.type === "entrance" || selectedGate.type === "exit")
    ) {
      applyManualRegistrationConfig(selectedGate.type);
    }
  }, [
    accessGroups,
    applyManualRegistrationConfig,
    registerType,
    selectedAccessPointId,
    selectedGate?.type,
    step,
  ]);

  const defaultManual = () => {
    setRequirementsValues([]);
    setScheduleStatus("allowed");
    setSelectedRule(null);
    setActiveRequirements([]);
    setQrScheduleStatus("allowed");
    setQrResolvedRule(null);
    setQrActiveRequirements([]);
    setCountryCode("+52");
    setVisitorPhone("");
    setIdFile(null);
    setPlateFile(null);
    setEquipmentFile(null);
    setPortraitFile(null);
    setVisitorName("");
    setUnit(null);
    setUnitInputValue("");
    setSelectedUser(null);
    setUserInputValue("");
    setMotive("");
    setVisitorSearch("");
    setSelectedPhoto(null);
    setSelectedAccessToExit(null);
    setMessage("");
    setValidQr(false);
    setCurrentAccessKey(null);
    setIsEntrance(false);
    setQrStatusValue(null);
  };

  React.useEffect(() => {
    if (photos && photos.length > 0) {
      const photoId =
        photos.filter((item) => item?.type === "photo id")[0] || null;
      const photoPlates =
        photos.filter((item) => item?.type === "plates")[0] || null;
      const photoVisitor =
        photos.filter((item) => item?.type === "visitor")[0] || null;
      const photoEquipment =
        photos.filter((item) => item?.type === "equipment")[0] || null;

      if (photoId) {
        setSelectedPhoto(photoId);
      } else if (photoPlates) {
        setSelectedPhoto(photoPlates);
      } else if (photoVisitor) {
        setSelectedPhoto(photoVisitor);
      } else if (photoEquipment) {
        setSelectedPhoto(photoEquipment);
      }
    }
  }, [photos]);

  React.useEffect(() => {
    if (selectedAccessPointGuard) {
      getAccessGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccessPointGuard]);

  const getAccessGroups = async () => {
    if (selectedAccessPointGuard) {
      try {
        const response = await api.accessGroupsByAccessPointRequest(
          Number(selectedAccessPointGuard),
        );
        if (response) {
          setAccessGroups(response?.data ?? []);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  // const getAccessGroups = React.useCallback(async () => {
  //   if (selectedAccessPointGuard) {
  //     try {
  //       const response = await api.accessGroupsByAccessPointRequest(
  //         Number(selectedAccessPointGuard),
  //       );
  //       if (response) {
  //         const accessGroupResponse = await api.accessGroupsByIdRequest(
  //           response?.data[0]?.id,
  //         );
  //         if (accessGroupResponse) {
  //           const accsesGroupRequierements =
  //             accessGroupResponse.data?.attributes?.requirement_types?.data.map(
  //               (item) => item,
  //             );
  //           if (accsesGroupRequierements.length > 0) {
  //             const filteredRequirements = accsesGroupRequierements.filter(
  //               (item) => item.attributes.transaction === true,
  //             );
  //             if (filteredRequirements.length > 0) {
  //               setRequirements(accsesGroupRequierements);
  //             }
  //           }
  //         }
  //         const newData = response?.data.flatMap((item) => {
  //           const roleIds =
  //             item?.attributes?.role_permissions_users?.data.map(
  //               (role) => role.id,
  //             ) || [];
  //           return item?.attributes?.configuration.map((config) => ({
  //             ...config,
  //             roleIds,
  //           }));
  //         });
  //         const filteredBySelectedAccessPoint = newData.filter(
  //           (item) => item?.id === Number(selectedAccessPointGuard),
  //         );
  //         const fixedResponse: AccessGroupCurrent[] =
  //           filteredBySelectedAccessPoint.map((item) => {
  //             return {
  //               daysAllowed: item?.week_days,
  //               antipassback: item?.antipassback,
  //               register: item?.register,
  //               photographs: item?.photos,
  //               fromHour: item?.start_time.slice(0, -3),
  //               toHour: item?.end_time.slice(0, -3),
  //               roles: item?.roleIds,
  //             };
  //           });
  //         const visitorsRules = fixedResponse.filter(
  //           (item) => item?.roles.includes(18) || item?.roles.includes(17),
  //         );
  //         setRules(visitorsRules);
  //       }
  //     } catch (error: any) {
  //       console.error(error);
  //       const errorMessage = error?.response?.data?.error?.message;
  //       showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
  //     }
  //   }
  // }, [dispatch, labels.generic_error, selectedAccessPointGuard]);

  // React.useEffect(() => {
  //   getAccessGroups();
  // }, [getAccessGroups]);

  const getAccessPointData = React.useCallback(async () => {
    const selectedAccessPointDocumentId = user?.access_points?.find(
      (item) => item?.id === Number(selectedAccessPointGuard),
    )?.documentId;
    if (selectedAccessPointGuard && selectedAccessPointDocumentId) {
      try {
        setIsLoading(true);
        const response = await api.accessPointByIdRequest(
          selectedAccessPointDocumentId,
        );
        if (response) {
          const fixedResponse = response.data;
          const resGates = fixedResponse?.gates || null;
          setGates(sortGatesEntranceFirst(resGates));
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setGates(null);
    }
  }, [
    dispatch,
    labels.generic_error,
    selectedAccessPointGuard,
    user?.access_points,
  ]);

  React.useEffect(() => {
    getAccessPointData();
  }, [getAccessPointData]);

  const getUnits = React.useCallback(async () => {
    if (propertyId && registerType === "manual" && step === "step_2") {
      try {
        setIsLoading(true);
        const response = await api.unitsRequest(Number(propertyId), 1, 100);
        if (response) {
          const array: UnitInfo[] = response.data.map((item, index) => {
            return {
              key: `${item?.id}-${index}`,
              label: item.name,
              id: item?.id,
            };
          });
          setUnitsArray(array);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [dispatch, labels.generic_error, propertyId, registerType, step]);

  React.useEffect(() => {
    getUnits();
  }, [getUnits]);

  React.useEffect(() => {
    let active = true;
    setUsersArray([]);
    setSelectedUser(null);
    setUserInputValue("");
    setUnit(null);
    setUnitInputValue("");
    if (!propertyId || registerType !== "manual" || step !== "step_2") return;
    api.manualRegistrationUsersRequest(propertyId)
      .then((users) => {
        if (!active) return;
        setUsersArray(users.filter((item) => item.unit?.id).map((item) => ({
          key: `${item.id}`,
          id: item.id,
          documentId: item.documentId,
          label: item.name || item.username,
          unit: {
            key: `${item.unit!.id}`,
            id: item.unit!.id,
            label: item.unit!.name,
          },
        })));
      })
      .catch((error) => {
        if (active) showError(dispatch, error?.response?.data?.error?.message || labels.generic_error);
      });
    return () => { active = false; };
  }, [propertyId, registerType, step, dispatch, labels.generic_error]);

  const getEntraceTransactions = React.useCallback(
    async (debouncedText: string) => {
      if (propertyId) {
        try {
          setAccessesLoading(true);
          const response = await api.getNoExitAccessesRequest(
            propertyId,
            debouncedText,
          );
          if (response) {
            setAccessesToExit(response?.data);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setAccessesLoading(false);
        }
      }
    },
    [dispatch, labels.generic_error, propertyId],
  );

  React.useEffect(() => {
    if (
      selectedGate?.type === "exit" &&
      step === "step_2" &&
      registerType === "manual"
    ) {
      getEntraceTransactions(debouncedVisitor);
    }
  }, [
    debouncedVisitor,
    getEntraceTransactions,
    registerType,
    selectedGate?.type,
    step,
  ]);

  const applyQrRegistrationConfig = (
    accessKey: NormalAccessKeyData | null,
    isValid: boolean,
    gateType: "entrance" | "exit" | undefined,
  ) => {
    if (!isValid || !accessKey || !gateType || !selectedAccessPointId) {
      setQrScheduleStatus("allowed");
      setQrResolvedRule(null);
      setQrActiveRequirements([]);
      return;
    }

    if (accessKey.access_key_type === "authorization") {
      setQrScheduleStatus("allowed");
      setQrResolvedRule(null);
      setQrActiveRequirements([]);
      return;
    }

    const resolved = resolveRegistrationConfig(
      accessKey.access_groups ?? [],
      selectedAccessPointId,
      gateType,
      new Date(),
      { onlyVisitorGroups: false },
    );
    setQrScheduleStatus(resolved.status);
    setQrResolvedRule(resolved.rule);
    setQrActiveRequirements(resolved.requirements);
  };

  const onReadCode = async (result: IDetectedBarcode[]) => {
    const resultUuid = result[0]?.rawValue;
    if (resultUuid && selectedGate?.id && propertyId) {
      try {
        const validateResponse = await api.validateKey({
          gate_id: selectedGate.id,
          key: resultUuid,
        });
        if (validateResponse) {
          let accessKeyVerifiable = "";
          const accessKeyByIdentifier = await api.getInvitationByIdentifier2(
            resultUuid,
            propertyId,
          );
          if (accessKeyByIdentifier.length > 0) {
            const activeAccessKey = accessKeyByIdentifier.find(
              (item) => item.daleted_at === null,
            );
            if (activeAccessKey) {
              accessKeyVerifiable = activeAccessKey?.verifiable_ids[0]?.uuid;
            } else {
              const deactiveAccessKey = accessKeyByIdentifier.find(
                (item) => item.daleted_at !== null,
              );
              if (deactiveAccessKey) {
                accessKeyVerifiable =
                  deactiveAccessKey?.verifiable_ids[0]?.uuid;
              }
            }
          } else {
            accessKeyVerifiable = resultUuid;
          }
          const accessKeyByVerifiable =
            await api.verifiableIdByUuidRequest(accessKeyVerifiable);

          const accessKeyData =
            accessKeyByVerifiable && accessKeyByVerifiable?.data.length > 0
              ? accessKeyByVerifiable?.data[0]?.access_key_qr
              : null;

          if (accessKeyData) {
            setCurrentAccessKey(accessKeyData);
            setRequirementsValues([]);
          }
          let isValidKey = false;
          let currentStatus: {
            message: {
              en: string;
              es: string;
            };
            type: "entrance" | "exit" | "try";
          } | null = null;
          if (
            validateResponse?.message?.en === "Exit authorized." ||
            validateResponse?.message?.en === "Entrance authorized." ||
            validateResponse?.message?.en === "Access authorized.." ||
            validateResponse?.message?.en === "Exit authorized.."
          ) {
            if (
              validateResponse?.message?.en === "Entrance authorized." ||
              validateResponse?.message?.en === "Access authorized.."
            ) {
              setIsEntrance(true);
              currentStatus = {
                message: {
                  en: validateResponse?.message?.en,
                  es: validateResponse?.message?.es,
                },
                type: "entrance",
              };
            }
            if (
              validateResponse?.message?.en === "Exit authorized." ||
              validateResponse?.message?.en === "Exit authorized.."
            ) {
              setIsEntrance(false);
              currentStatus = {
                message: {
                  en: validateResponse?.message?.en,
                  es: validateResponse?.message?.es,
                },
                type: "exit",
              };
            }
            setValidQr(true);
            isValidKey = true;
          } else {
            // Acceso NO autorizado
            currentStatus = {
              message: {
                en: validateResponse?.message?.en,
                es: validateResponse?.message?.es,
              },
              type: "try",
            };
            setValidQr(false);
            applyQrRegistrationConfig(null, false, undefined);
            if (hasUserAccessGroupsPerms) {
              const userByUuid = await getAccessKeyByUuid(resultUuid);
              const userByIdentifier =
                await getAccessKeyByIdentifier(resultUuid);
              if (userByUuid || userByIdentifier) {
                if (userByUuid) {
                  setUserQrId(userByUuid?.userId);
                  setUserQrUnit(userByUuid?.unitId);
                } else if (userByIdentifier) {
                  setUserQrId(userByIdentifier?.userId);
                  setUserQrUnit(userByIdentifier?.unitId);
                } else {
                  setUserQrId(null);
                  setUserQrUnit(null);
                }
              } else {
                setUserQrId(null);
                setUserQrUnit(null);
              }
            }
          }
          const gateType: "entrance" | "exit" | undefined =
            selectedGate?.type === "exit"
              ? "exit"
              : selectedGate?.type === "entrance"
                ? "entrance"
                : undefined;
          applyQrRegistrationConfig(accessKeyData ?? null, isValidKey, gateType);
          setQrStatusValue(currentStatus);
          setMessage(validateResponse?.message[currentLanguage]);
          setStep("step_3");
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const getAccessKeyByUuid = async (uuid: string) => {
    try {
      const accessKeyResponse = await api.verifiableIdByUuidRequest(uuid);
      if (accessKeyResponse && accessKeyResponse?.data?.length > 0) {
        const qrAccessKeyResponse =
          accessKeyResponse?.data[0]?.access_key_qr;
        const accessKeyType = qrAccessKeyResponse?.access_key_type;
        if (accessKeyType === "user") {
          const accessKeyUserId =
            qrAccessKeyResponse?.verified_visitor?.id;
          const accessKeyUnitId =
            qrAccessKeyResponse?.unit?.id;
          if (accessKeyUserId && accessKeyUnitId) {
            return { userId: accessKeyUserId, unitId: accessKeyUnitId };
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const getAccessKeyByIdentifier = async (identifier: string) => {
    if (!propertyId) return null;
    try {
      const accessKeyResponse = await api.getInvitationByIdentifier(
        identifier,
        propertyId,
      );
      if (accessKeyResponse && accessKeyResponse?.length > 0) {
        const qrAccessKeyResponse: AccessKeyCreateResponse =
          accessKeyResponse[0];
        const accessKeyType = qrAccessKeyResponse?.access_key_type;
        if (accessKeyType === "user") {
          const accessKeyUserId = qrAccessKeyResponse?.verified_visitor?.id;
          const accessKeyUnitId = qrAccessKeyResponse?.unit?.id;
          if (accessKeyUserId && accessKeyUnitId) {
            return { userId: accessKeyUserId, unitId: accessKeyUnitId };
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  // const onReadCode = async (result: IDetectedBarcode[]) => {
  //   const resultUuid = result[0]?.rawValue;
  //   if (resultUuid && selectedGate?.id && propertyId) {
  //     try {
  //       const validateResponse = await api.validateKey({
  //         gate_id: selectedGate.id,
  //         key: resultUuid,
  //       });
  //       if (validateResponse) {
  //         let accessKeyVerifiable = "";
  //         const accessKeyByIdentifier = await api.getInvitationByIdentifier(
  //           resultUuid,
  //           propertyId,
  //         );
  //         if (accessKeyByIdentifier.length > 0) {
  //           accessKeyVerifiable =
  //             accessKeyByIdentifier[0]?.verifiable_ids[0]?.uuid;
  //         } else {
  //           accessKeyVerifiable = resultUuid;
  //         }
  //         const accessKeyByVerifiable =
  //           await api.verifiableIdByUuidRequest(accessKeyVerifiable);
  //         if (accessKeyByVerifiable && accessKeyByVerifiable?.data.length > 0) {
  //           setCurrentAccessKey(
  //             accessKeyByVerifiable?.data[0]?.attributes?.access_key_qr?.data,
  //           );
  //         }
  //         let currentStatus: {
  //           message: {
  //             en: string;
  //             es: string;
  //           };
  //           type: "entrance" | "exit" | "try";
  //         } | null = null;
  //         if (
  //           validateResponse?.message?.en === "Exit authorized." ||
  //           validateResponse?.message?.en === "Entrance authorized." ||
  //           validateResponse?.message?.en === "Access authorized.." ||
  //           validateResponse?.message?.en === "Exit authorized.."
  //         ) {
  //           if (
  //             validateResponse?.message?.en === "Entrance authorized." ||
  //             validateResponse?.message?.en === "Access authorized.."
  //           ) {
  //             setIsEntrance(true);
  //             currentStatus = {
  //               message: {
  //                 en: validateResponse?.message?.en,
  //                 es: validateResponse?.message?.es,
  //               },
  //               type: "entrance",
  //             };
  //           }
  //           if (
  //             validateResponse?.message?.en === "Exit authorized." ||
  //             validateResponse?.message?.en === "Exit authorized.."
  //           ) {
  //             setIsEntrance(false);
  //             currentStatus = {
  //               message: {
  //                 en: validateResponse?.message?.en,
  //                 es: validateResponse?.message?.es,
  //               },
  //               type: "exit",
  //             };
  //           }
  //           setValidQr(true);
  //         } else {
  //           currentStatus = {
  //             message: {
  //               en: validateResponse?.message?.en,
  //               es: validateResponse?.message?.es,
  //             },
  //             type: "try",
  //           };
  //           setValidQr(false);
  //         }
  //         setQrStatusValue(currentStatus);
  //         setMessage(validateResponse?.message[currentLanguage]);
  //         setStep("step_3");
  //       }
  //     } catch (error: any) {
  //       console.error(error);
  //       const errorMessage = error?.response?.data?.error?.message;
  //       showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
  //     }
  //   }
  // };

  const sendAuthRequest = async () => {
    if (userQrId && userQrUnit && selectedAccessPointGuard) {
      try {
        setIsLoading(true);
        const createAuthorizationPassPayload: CreateAuthorizationPassPayload = {
          data: {
            user: userQrId,
            unit: userQrUnit,
            access_point: Number(selectedAccessPointGuard),
            type: selectedGate?.type === "entrance" ? "entrance" : "exit",
          },
        };
        const createAuthorizationPassResponse =
          await api.createAuthorizationPass(createAuthorizationPassPayload);
        if (createAuthorizationPassResponse) {
          const sendAuthPayload: SendAuthorizationRequest = {
            pass_id: createAuthorizationPassResponse.documentId,
          };
          const sendAuthResponse =
            await api.sendAuthorizationRequest(sendAuthPayload);
          if (sendAuthResponse) {
            if (sendAuthResponse?.success) {
              setShowSendSuccessModal(true);
            } else {
              showError(dispatch, labels.authorization_sent_error);
            }
          } else {
            showError(dispatch, labels.authorization_sent_error);
          }
        } else {
          showError(dispatch, labels.authorization_sent_error);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const qrLog = async () => {
    if (currentAccessKey && selectedGate && selectedAccessPointId) {
      const doorType = selectedGate.type; //entrance or exit
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      try {
        setIsLoading(true);
        const transactionPhotosIds: number[] = [];
        const currentDateTime = dayjs().toISOString();
        const visitor = currentAccessKey?.one_time_visitor?.provider?.name
          ? currentAccessKey?.one_time_visitor?.provider?.name
          : currentAccessKey?.one_time_visitor?.provider_name
            ? currentAccessKey?.one_time_visitor?.provider_name
            : currentAccessKey?.one_time_visitor?.name
              ? currentAccessKey?.one_time_visitor?.name
              : currentAccessKey?.verified_visitor?.name
                ? currentAccessKey?.verified_visitor?.name
                : "";
        const requiredPhotos = qrResolvedRule?.photographs ?? [];
        let accessPayload: CreateAccessPayload = {
          data: {
            property: user?.property?.id,
            company: user?.company?.id,
            user: currentAccessKey?.created?.name,
            unit: currentAccessKey?.unit?.name,
            visitor: visitor,
            authorized: validQr,
            access_key_id: currentAccessKey?.id,
          },
        };
        let transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: doorType === "entrance" ? "entrance" : "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPointId,
            access_key_id: `${currentAccessKey?.id}`,
            users_permissions_users: guardIds,
            access_key_type: currentAccessKey?.access_key_type,
            status: qrStatusValue ? qrStatusValue : null,
          },
        };
        if (requiredPhotos.length > 0) {
          const transactionPhotosPayload: CreateAccessTransactionPhotoPayload[] =
            requiredPhotos.map((item) => {
              return {
                data: {
                  type:
                    item === "id"
                      ? "photo id"
                      : item === "plates"
                        ? "plates"
                        : item === "visitor"
                          ? "visitor"
                          : "equipment",
                },
              };
            });
          // Procesar cada transactionPhotosPayload
          for (const photoPayload of transactionPhotosPayload) {
            // Crear la transacción de foto
            const response =
              await api.createAccessTransactionPhoto(photoPayload);
            console.log("response", response);

            // Subir la foto asociada
            if (response?.data?.id) {
              transactionPhotosIds.push(response?.data?.id);
              const file =
                photoPayload?.data?.type === "photo id"
                  ? idFile
                  : photoPayload?.data?.type === "plates"
                    ? plateFile
                    : photoPayload?.data?.type === "equipment"
                      ? equipmentFile
                      : photoPayload?.data?.type === "visitor"
                        ? portraitFile
                        : "";
              if (file) {
                await api.uploadMediaRequest({
                  files: [file],
                  ref: "api::access-transaction-photo.access-transaction-photo",
                  refId: response?.data?.id,
                  field: "image",
                });
              } else {
                console.warn(
                  `No file provided for photo type: ${photoPayload?.data?.photo_type}`,
                );
              }
            }
          }
          transactionPayload = {
            data: {
              ...transactionPayload.data,
              access_transaction_photos: transactionPhotosIds,
            },
          };
        }
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          const transactionId = transactionResponse?.id;
          const transactionDocId = transactionResponse?.documentId;
          if (transactionId && requirementsValues?.length > 0) {
            for (const requ of requirementsValues) {
              const val = requ?.data?.value;
              if (val !== undefined && val !== null && String(val).trim() !== "") {
                await api.createRequirement({
                  data: {
                    requirement_type: requ?.data?.requirement_type,
                    access_transaction: { connect: [transactionDocId] },
                    value: String(val),
                  },
                });
              }
            }
          }
          if (doorType === "entrance") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                entrance_transaction: transactionId,
              },
            };
          }
          if (doorType === "exit") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                exit_transaction: transactionId,
              },
            };
          }
          if (currentAccessKey?.access_key_type === "authorization") {
            const accerssKeyPayload: CreateAccessKeyRequest = {
              daleted_at: currentDateTime,
            };
            await api.updateAccessKeyRequest(
              currentAccessKey?.id,
              accerssKeyPayload,
            );
          }
          const accessResponse = await api.createAccess(accessPayload);
          if (accessResponse) {
            const showPrintablePassFlow =
              doorType === "entrance" &&
              validQr === true &&
              ruleForPrintablePass?.printable_pass === true;
            if (showPrintablePassFlow) {
              const createdAttrs = currentAccessKey?.created;
              const receiveName =
                (createdAttrs?.name && String(createdAttrs.name).trim()) ||
                createdAttrs?.username ||
                "";
              setPrintablePassData({
                visitorName: visitor,
                receiveName,
                department: currentAccessKey?.unit?.name ?? "",
                registeredAt: currentDateTime,
                accessType: "entrance",
              });
              setPrintablePassOpen(true);
            } else {
              setStep("step_1");
              defaultManual();
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const exitQrLog = async () => {
    if (currentAccessKey && selectedGate && selectedAccessPointId && propertyId) {
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      const doorType = selectedGate.type; //entrance or exit
      if (selectedGate.type === "exit") {
        if (!validQr) {
          try {
            const visitor = currentAccessKey?.one_time_visitor?.provider?.name
              ? currentAccessKey?.one_time_visitor?.provider?.name
              : currentAccessKey?.one_time_visitor?.provider_name
                ? currentAccessKey?.one_time_visitor?.provider_name
                : currentAccessKey?.one_time_visitor?.name
                  ? currentAccessKey?.one_time_visitor?.name
                  : currentAccessKey?.verified_visitor?.name
                    ? currentAccessKey?.verified_visitor?.name
                    : "";
            setIsLoading(true);
            const currentDateTime = dayjs().toISOString();
            let transactionPayload: CreateTransactionPayload = {
              data: {
                datetime: currentDateTime,
                access_type: doorType === "entrance" ? "entrance" : "exit",
                gate: selectedGate?.id,
                // access_point: selectedAccessPointId,
                access_key_id: `${currentAccessKey?.id}`,
                users_permissions_users: guardIds,
                access_key_type: currentAccessKey?.access_key_type,
                status: qrStatusValue ? qrStatusValue : null,
              },
            };
            const transactionResponse =
              await api.createTransaction(transactionPayload);
            if (transactionResponse) {
              const transactionId = transactionResponse?.id;
              // if (
              //   qrRequirementTypes.length > 0 &&
              //   transactionId &&
              //   requirementsValues?.length > 0
              // ) {
              //   for (let i = 0; i < qrRequirementTypes.length; i++) {
              //     const val = requirementsValues[i]?.data?.value;
              //     if (
              //       val !== undefined &&
              //       val !== null &&
              //       String(val).trim() !== ""
              //     ) {
              //       await api.createRequirement({
              //         data: {
              //           requirement_type: qrRequirementTypes[i]?.id,
              //           access_key_qr: currentAccessKey?.id,
              //           access_transaction: transactionId,
              //           value: String(val),
              //         },
              //       });
              //     }
              //   }
              // }
              const accessCreatePayload: CreateAccessPayload = {
                data: {
                  property: user?.property?.id,
                  company: user?.company?.id,
                  user: currentAccessKey?.created?.name,
                  unit: currentAccessKey?.unit?.name,
                  visitor: visitor,
                  authorized: validQr,
                  access_key_id: currentAccessKey?.id,
                  exit_transaction: transactionId,
                },
              };
              const createAccess = await api.createAccess(accessCreatePayload);
              if (createAccess) {
                setStep("step_1");
                defaultManual();
              }
            }
          } catch (error: any) {
            console.error(error);
            const errorMessage = error?.response?.data?.error?.message;
            showError(
              dispatch,
              errorMessage ? errorMessage : labels.generic_error,
            );
          } finally {
            setIsLoading(false);
          }
        } else {
          const visitor = currentAccessKey?.one_time_visitor?.provider?.name
            ? currentAccessKey?.one_time_visitor?.provider?.name
            : currentAccessKey?.one_time_visitor?.provider_name
              ? currentAccessKey?.one_time_visitor?.provider_name
              : currentAccessKey?.one_time_visitor?.name
                ? currentAccessKey?.one_time_visitor?.name
                : currentAccessKey?.verified_visitor?.name
                  ? currentAccessKey?.verified_visitor?.name
                  : "";
          try {
            setIsLoading(true);
            const currentDateTime = dayjs().toISOString();
            const getAccess = await api.getAccessesByAccessKeyRequest(
              propertyId,
              currentAccessKey?.id,
            );
            let transactionPayload: CreateTransactionPayload = {
              data: {
                datetime: currentDateTime,
                access_type: doorType === "entrance" ? "entrance" : "exit",
                gate: selectedGate?.id,
                // access_point: selectedAccessPointId,
                access_key_id: `${currentAccessKey?.id}`,
                users_permissions_users: guardIds,
                access_key_type: currentAccessKey?.access_key_type,
                status: qrStatusValue ? qrStatusValue : null,
              },
            };
            const transactionResponse =
              await api.createTransaction(transactionPayload);
            if (transactionResponse) {
              const transactionId = transactionResponse?.id;
              const transactionDocId = transactionResponse?.documentId;
              if (
                qrRequirementTypes.length > 0 &&
                transactionId &&
                requirementsValues?.length > 0
              ) {
                for (let i = 0; i < qrRequirementTypes.length; i++) {
                  const val = requirementsValues[i]?.data?.value;
                  if (
                    val !== undefined &&
                    val !== null &&
                    String(val).trim() !== ""
                  ) {
                    await api.createRequirement({
                      data: {
                        requirement_type: qrRequirementTypes[i]?.id,
                        access_key_qr: currentAccessKey?.id,
                        access_transaction: { connect: [transactionDocId] },
                        value: String(val),
                      },
                    });
                  }
                }
              }
              if (currentAccessKey?.access_key_type === "authorization") {
                const accerssKeyPayload: CreateAccessKeyRequest = {
                  daleted_at: currentDateTime,
                };
                await api.updateAccessKeyRequest(
                  currentAccessKey?.id,
                  accerssKeyPayload,
                );
              }
              if (getAccess?.data && getAccess?.data?.length > 0) {
                const accessPayload: CreateAccessPayload = {
                  data: {
                    exit_transaction: transactionId,
                  },
                };
                const updateAccess = await api.updateAccess(
                  getAccess?.data[0]?.documentId,
                  accessPayload,
                );
                if (updateAccess) {
                  setStep("step_1");
                  defaultManual();
                }
              } else {
                const accessCreatePayload: CreateAccessPayload = {
                  data: {
                    property: user?.property?.id,
                    company: user?.company?.id,
                    user: currentAccessKey?.created?.name,
                    unit: currentAccessKey?.unit?.name,
                    visitor: visitor,
                    authorized: validQr,
                    access_key_id: currentAccessKey?.id,
                    exit_transaction: transactionId,
                  },
                };
                const createAccess =
                  await api.createAccess(accessCreatePayload);
                if (createAccess) {
                  setStep("step_1");
                  defaultManual();
                }
              }
            }
          } catch (error: any) {
            console.error(error);
            const errorMessage = error?.response?.data?.error?.message;
            showError(
              dispatch,
              errorMessage ? errorMessage : labels.generic_error,
            );
          } finally {
            setIsLoading(false);
          }
        }
      }
    }
  };

  const manualLog = async () => {
    if (!selectedGate || !selectedGate.type || !selectedAccessPointId) return;

    const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
    if (!hasRegisterAuthorization) {
      if (!selectedUser?.documentId) return;
      try {
        setIsLoading(true);
        const createAuthorizationPassPayload: CreateAuthorizationPassPayload = {
          data: {
            visitor_name: visitorName,
            visitor_phone: `${countryCode}${visitorPhone}`,
            unit: unit?.id,
            access_point: selectedAccessPointId,
            type: "visitor",
          },
        };
        const createAuthorizationPassResponse =
          await api.createAuthorizationPass(createAuthorizationPassPayload);
        if (createAuthorizationPassResponse) {
          const sendAuthPayload: SendAuthorizationRequest = {
            pass_id: createAuthorizationPassResponse.documentId,
            receiver_docid: selectedUser.documentId,
          };
          const sendAuthResponse =
            await api.sendAuthorizationRequest(sendAuthPayload);
          if (sendAuthResponse) {
            if (sendAuthResponse?.success) {
              setShowSendSuccessModal(true);
            } else {
              showError(dispatch, labels.authorization_sent_error);
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    } else {
      const doorType = selectedGate.type; //entrance or exit
      try {
        setIsLoading(true);
        const transactionPhotosIds: number[] = [];
        const currentDateTime = dayjs().toISOString();
        let accessPayload: CreateAccessPayload = {
          data: {
            property: user?.property?.id,
            company: user?.company?.id,
            user: selectedUser?.label,
            unit: unit?.label,
            visitor: visitorName,
            authorized: true,
          },
        };
        let transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: doorType === "entrance" ? "entrance" : "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPointId,
            users_permissions_users: guardIds,
          },
        };
        if (selectedRule?.photographs) {
          const transactionPhotosPayload: CreateAccessTransactionPhotoPayload[] =
            selectedRule?.photographs.map((item) => {
              return {
                data: {
                  type:
                    item === "id"
                      ? "photo id"
                      : item === "plates"
                        ? "plates"
                        : item === "visitor"
                          ? "visitor"
                          : "equipment",
                },
              };
            });
          // Procesar cada transactionPhotosPayload
          for (const photoPayload of transactionPhotosPayload) {
            // Crear la transacción de foto
            const response =
              await api.createAccessTransactionPhoto(photoPayload);

            // Subir la foto asociada
            if (response?.data?.id) {
              transactionPhotosIds.push(response?.data?.id);
              const file =
                photoPayload?.data?.type === "photo id"
                  ? idFile
                  : photoPayload?.data?.type === "plates"
                    ? plateFile
                    : photoPayload?.data?.type === "equipment"
                      ? equipmentFile
                      : photoPayload?.data?.type === "visitor"
                        ? portraitFile
                        : "";
              if (file) {
                await api.uploadMediaRequest({
                  files: [file],
                  ref: "api::access-transaction-photo.access-transaction-photo",
                  refId: response?.data?.id,
                  field: "image",
                });
              } else {
                console.warn(
                  `No file provided for photo type: ${photoPayload?.data?.photo_type}`,
                );
              }
            }
          }
          transactionPayload = {
            data: {
              ...transactionPayload.data,
              access_transaction_photos: transactionPhotosIds,
            },
          };
        }
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          console.log("transactionResponse", transactionResponse);
          const transactionId = transactionResponse?.id;
          const transactionDocId = transactionResponse?.documentId;
          if (doorType === "entrance") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                entrance_transaction: transactionResponse?.id,
              },
            };
          }
          if (doorType === "exit") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                exit_transaction: transactionResponse?.id,
              },
            };
          }
          if (transactionId && requirementsValues?.length > 0) {
            console.log("requirementsValues", requirementsValues);
            for (const requ of requirementsValues) {
              const val = requ?.data?.value;
              if (val !== undefined && val !== null && String(val).trim() !== "") {
                await api.createRequirement({
                  data: {
                    requirement_type: requ?.data?.requirement_type,
                    access_transaction: { connect: [transactionDocId] },
                    value: String(val),
                  },
                });
              }
            }
          }
          const accessResponse = await api.createAccess(accessPayload);
          if (accessResponse) {
            const showPrintablePassFlow =
              doorType === "entrance" &&
              ruleForPrintablePass?.printable_pass === true;
            if (showPrintablePassFlow) {
              setPrintablePassData({
                visitorName,
                receiveName: selectedUser?.label ?? "",
                department: unit?.label ?? "",
                registeredAt: currentDateTime,
                accessType: "entrance",
              });
              setPrintablePassOpen(true);
            } else {
              setStep("step_1");
              defaultManual();
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onLogExit = async () => {
    if (selectedAccessToExit && selectedGate && selectedAccessPointId) {
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      try {
        setIsLoading(true);
        const currentDateTime = dayjs().toISOString();
        const currentAccessId = selectedAccessToExit?.documentId;
        const transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPointId,
            users_permissions_users: guardIds,
          },
        };
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          const accessPayload: CreateAccessPayload = {
            data: {
              exit_transaction: transactionResponse?.id,
            },
          };
          const updateAccess = await api.updateAccess(
            currentAccessId,
            accessPayload,
          );
          if (updateAccess) {
            setStep("step_1");
            defaultManual();
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="mobile-module-body" style={{ position: "relative" }}>
      <PrintablePass open={printablePassOpen} setOpen={setPrintablePassOpen} data={printablePassData} onFinished={finishPrintablePassFlow} />
      <AuthorizationRequestSendedModal
        open={sendSuccessModal}
        setOpen={setShowSendSuccessModal}
        onClose={() => {
          setUserQrId(null);
          setUserQrUnit(null);
          setStep("step_1");
          defaultManual();
        }}
      />
      <ImageModal
        showModal={showPictureModal}
        setShowModal={setShowPictureModal}
        srcImage={toAbsoluteMediaUrl(selectedPhoto?.url)}
        imageType={`${selectedPhoto?.type}`}
        visitorName={`${selectedAccessToExit?.visitor}`}
      />
      <div
        style={{
          zIndex: 99,
          position: "relative",
        }}
      >
        <MobileModuleHeader
          title={labels.log}
          titleIcon={"material-symbols:checkbook"}
          isGuardLog={true}
          registerType={registerType}
          setRegisterType={setRegisterType}
          searchValue={searchFilter}
          setSearchValue={setSearchFilter}
          hasSearch={true}
          hasFilters={true}
          isDowloadable={true}
          hasNewButton={false}
        />
      </div>
      {showLogButton && (
        <div
          className="w-100 border-box flex align-items-center justify-content-between gap-4 px-4"
          style={{
            height: "auto",
            position: "absolute",
            bottom: 0,
            paddingBottom: 8,
            paddingTop: 8,
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            zIndex: 100,
          }}
        >
          <div className={`w-50 border-box`}>
            <Button
              fullWidth
              size="small"
              disableElevation
              onClick={() => {
                setStep("step_1");
                defaultManual();
              }}
            >
              {labels.cancel}
            </Button>
          </div>
          <div className={`w-50 border-box`}>
            {registerType === "manual" && selectedGate?.type !== "exit" && (
              <Button
                fullWidth
                size="small"
                variant="contained"
                disableElevation
                disabled={disabledManualLog}
                onClick={manualLog}
              >
                {!hasRegisterAuthorization
                  ? labels.send_for_authorization
                  : labels.generate_log}
              </Button>
            )}
            {!hasUserAccessGroupsPerms && registerType === "qr" && (
              <Button
                fullWidth
                size="small"
                variant="contained"
                disableElevation
                disabled={
                  selectedGate?.type !== "exit"
                    ? disabledQrEntranceLog
                    : disabledQrExitLog
                }
                onClick={selectedGate?.type !== "exit" ? qrLog : exitQrLog}
              >
                {labels.generate_log}
              </Button>
            )}
            {hasUserAccessGroupsPerms && registerType === "qr" && validQr && (
              <Button
                fullWidth
                size="small"
                variant="contained"
                disableElevation
                disabled={
                  selectedGate?.type !== "exit"
                    ? disabledQrEntranceLog
                    : disabledQrExitLog
                }
                onClick={selectedGate?.type !== "exit" ? qrLog : exitQrLog}
              >
                {labels.generate_log}
              </Button>
            )}
          </div>
        </div>
      )}
      <div
        className={"mobile-module-body-no-filters-content"}
        style={{ zIndex: 10, position: "relative" }}
      >
        {isLoading ? (
          <div className="w-100 h-100 flex justify-content-center align-items-center">
            <CircularProgress />
          </div>
        ) : (
          <>
            {step === "step_1" && (
              <div className="border-box flex flex-dir-col align-items-center gap-4">
                <p
                  className="body-text-md mb-6"
                  style={{ textAlign: "center" }}
                >
                  {labels.select_gate}
                </p>
                {visibleGates &&
                  visibleGates.map((item, index) => (
                    <div
                      key={`${item?.id}-${index}`}
                      className="w-100 card border-box p-1"
                      onClick={() => {
                        const fixedGate: GateInfo = {
                          id: item?.id,
                          name: item?.name,
                          type: item?.type,
                        };
                        setSelectedGate(fixedGate);
                        if (
                          registerType === "manual" &&
                          (fixedGate.type === "entrance" ||
                            fixedGate.type === "exit")
                        ) {
                          applyManualRegistrationConfig(fixedGate.type);
                        }
                        setStep("step_2");
                      }}
                    >
                      <div
                        className={`border-box flex justify-content-between align-items-center p-4 py-7 ${
                          item?.type === "exit"
                            ? "exit-border"
                            : "entrance-border"
                        }`}
                      >
                        <div className="border-box flex align-items-center">
                          <div className="border-box flex align-items-center">
                            {item?.type === "entrance" && (
                              <IconifyIcon
                                icon={"majesticons:door-enter"}
                                className="entrance-color"
                                style={{
                                  fontSize: 40,
                                  transform: "scaleX(-1)",
                                }}
                              />
                            )}
                            {item?.type === "exit" && (
                              <IconifyIcon
                                icon={"majesticons:door-exit"}
                                className="exit-color"
                                style={{
                                  fontSize: 40,
                                }}
                              />
                            )}
                          </div>
                          <div className="ml-6">
                            {item?.type === "entrance" && (
                              <p className="body-text-sm entrance-color">
                                {labels.entrance}
                              </p>
                            )}
                            {item?.type === "exit" && (
                              <p className="body-text-sm exit-color">
                                {labels.exit}
                              </p>
                            )}
                            <p className="mt-2">{item?.name}</p>
                          </div>
                        </div>
                        <div
                          className="border-box flex align-items-center justify-content-end"
                          style={{ minWidth: 40 }}
                        >
                          {registerType === "qr" && (
                            <IconifyIcon
                              icon={"arcticons:qr"}
                              style={{ fontSize: 30 }}
                            />
                          )}
                          {registerType === "manual" && (
                            <IconifyIcon
                              icon={"arcticons:simple-clipboard-editor"}
                              style={{ fontSize: 30 }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {step === "step_2" && (
              <>
                {registerType === "qr" && (
                  <div className="w-100 border-box flex flex-dir-col align-items-center gap-4">
                    <p
                      className="body-text-md mb-6"
                      style={{ textAlign: "center" }}
                    >
                      {labels.qr_to_camera}
                    </p>
                    <Button
                      size="small"
                      className="px-8"
                      disableElevation
                      onClick={() => {
                        setStep("step_1");
                      }}
                    >
                      {labels.cancel}
                    </Button>
                    <div className="w-90" style={{ maxWidth: 400 }}>
                      <Scanner
                        allowMultiple
                        scanDelay={1000}
                        onScan={(result) => onReadCode(result)}
                        constraints={constraints}
                      />
                    </div>
                  </div>
                )}
                {registerType === "manual" && (
                  <div
                    className="w-100 border-box flex flex-dir-col align-items-center gap-4"
                    style={{ paddingBottom: 70 }}
                  >
                    {!manualScheduleAllowed ? (
                      <>
                        <Alert severity="warning" sx={{ width: "100%" }}>
                          {scheduleWarningMessage}
                        </Alert>
                        <Button
                          size="small"
                          className="px-8 mt-4"
                          disableElevation
                          onClick={() => {
                            setStep("step_1");
                            defaultManual();
                          }}
                        >
                          {labels.cancel}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p
                          className="body-text-md mb-6"
                          style={{ textAlign: "center" }}
                        >
                          {selectedGate?.type === "entrance"
                            ? labels.enter_log_data
                            : labels.search_and_select_visitor}
                        </p>
                        {selectedGate?.type === "exit" && (
                          <ManualExitLog
                            visitorSearch={visitorSearch}
                            setVisitorSearch={setVisitorSearch}
                            selectedAccessToExit={selectedAccessToExit}
                            setSelectedAccessToExit={setSelectedAccessToExit}
                            photos={photos}
                            selectedPhoto={selectedPhoto}
                            setSelectedPhoto={setSelectedPhoto}
                            setShowPictureModal={setShowPictureModal}
                            accessesToExit={accessesToExit}
                            accessesLoading={accessesLoading}
                            onLogExit={onLogExit}
                          />
                        )}
                        {selectedGate?.type === "entrance" && (
                          <ManualEntranceLog
                            hasRegisterAuthorization={hasRegisterAuthorization}
                            visitorName={visitorName}
                            setVisitorName={setVisitorName}
                            countryCode={countryCode}
                            setCountryCode={setCountryCode}
                            visitorPhone={visitorPhone}
                            setVisitorPhone={setVisitorPhone}
                            defaultUnitsProps={defaultUnitsProps}
                            unit={unit}
                            setUnit={setUnit}
                            unitInputValue={unitInputValue}
                            setUnitInputValue={setUnitInputValue}
                            defaultUsersProps={defaultUsersProps}
                            selectedUser={selectedUser}
                            setSelectedUser={setSelectedUser}
                            userInputValue={userInputValue}
                            setUserInputValue={setUserInputValue}
                            motive={motive}
                            setMotive={setMotive}
                            requirements={activeRequirements}
                            requirementsValues={requirementsValues}
                            setRequirementsValues={setRequirementsValues}
                            selectedRule={selectedRule}
                            idFile={idFile}
                            setIdFile={setIdFile}
                            plateFile={plateFile}
                            setPlateFile={setPlateFile}
                            portraitFile={portraitFile}
                            setPortraitFile={setPortraitFile}
                            equipmentFile={equipmentFile}
                            setEquipmentFile={setEquipmentFile}

                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
            {step === "step_3" && (
              <>
                {registerType === "qr" && (
                  <div
                    className="w-100 border-box flex flex-dir-col align-items-center gap-4"
                    style={{ paddingBottom: 70, paddingTop: 20 }}
                  >
                    {message && (
                      <>
                        {validQr ? (
                          <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                            <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                              <div className="w-100 flex align-items-center justify-content-center gap-4 mb-4">
                                <IconifyIcon
                                  icon={"lets-icons:check-fill"}
                                  style={{ fontSize: 20 }}
                                  className="green-color"
                                />
                                <p
                                  className={`text-center font-18 font-w-700 green-color uppercase`}
                                >
                                  {labels.authorized}
                                </p>
                              </div>
                              <p
                                className={`text-center font-14 font-w-500 green-color`}
                              >
                                {message}
                              </p>
                            </div>
                            {currentAccessKey && (
                              <div className="w-100 pt-10">
                                <p className="body-text-lg mb-4 font-w-700">
                                  {labels.visitor}:{" "}
                                  {currentAccessKey?.verified_visitor?.id
                                    ? currentAccessKey?.verified_visitor
                                        ?.username
                                    : currentAccessKey?.one_time_visitor
                                          ?.provider?.name
                                      ? currentAccessKey?.one_time_visitor
                                          ?.provider?.name
                                      : currentAccessKey?.one_time_visitor
                                            ?.provider_name
                                        ? currentAccessKey?.one_time_visitor
                                            ?.provider_name
                                        : currentAccessKey?.one_time_visitor
                                              ?.name
                                          ? currentAccessKey?.one_time_visitor
                                              ?.name
                                          : ""}
                                </p>
                                <p className="body-text-md mb-6 font-w-700">
                                  {labels.reason_for_visit}:{" "}
                                  {currentAccessKey?.reason_for_visit}
                                </p>
                                <p className="body-text-md mb-4 mt-10">
                                  {labels.user}:{" "}
                                  {currentAccessKey?.created?.username}
                                </p>
                                <p className="body-text-md mb-6">
                                  {labels.unit}:{" "}
                                  {currentAccessKey?.unit?.name}
                                </p>
                              </div>
                            )}
                            {isEntrance && qrRequirementTypes.map((requirement, index) => (
 <RequirementsComponent key={requirement.id} requirement={requirement} requirementIndex={index} value={requirementsValues} setValue={setRequirementsValues} />
))}
{!qrScheduleAllowed && (
                              <Alert severity="warning" sx={{ width: "100%", mt: 4 }}>
                                {qrScheduleWarningMessage}
                              </Alert>
                            )}
                            {isEntrance &&
                              currentAccessKey &&
                              qrScheduleAllowed &&
                              accessKeyPhotos.length > 0 && (
                                <>
                                  <Divider className="w-100 pt-10 pb-10">
                                    <p className="subtitle-color font-14">
                                      {labels.required_photographs} *
                                    </p>
                                  </Divider>
                                  <div className="w-100 border-box flex align-items-center justify-content-evenly gap-6 mt-4">
                                    {accessKeyPhotos.map(
                                      (item, index) => (
                                        <PhotoButton
                                          key={`${item}-${index}`}
                                          photoType={
                                            item as
                                              | "id"
                                              | "plates"
                                              | "visitor"
                                              | "other"
                                          }
                                          file={
                                            item === "id"
                                              ? idFile
                                              : item === "plates"
                                                ? plateFile
                                                : item === "visitor"
                                                  ? portraitFile
                                                  : item === "other"
                                                    ? equipmentFile
                                                    : null
                                          }
                                          setFile={
                                            item === "id"
                                              ? setIdFile
                                              : item === "plates"
                                                ? setPlateFile
                                                : item === "visitor"
                                                  ? setPortraitFile
                                                  : item === "other"
                                                    ? setEquipmentFile
                                                    : () => {}
                                          }
                                        />
                                      ),
                                    )}
                                  </div>
                                </>
                              )}
                          </div>
                        ) : (
                          <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                            <div className="w-100 flex align-items-center justify-content-center gap-4 mb-4">
                              <IconifyIcon
                                icon={"entypo:block"}
                                style={{ fontSize: 20 }}
                                className="red-color"
                              />
                              <p
                                className={`text-center font-18 font-w-700 red-color uppercase`}
                              >
                                {labels.not_authorized}
                              </p>
                            </div>
                            <p
                              className={`text-center font-14 font-w-500 red-color`}
                            >
                              {message}
                            </p>
                            {hasUserAccessGroupsPerms && userQrId && (
                              <div className="w-100 flex justify-content-center mt-10">
                                <Button
                                  variant="contained"
                                  onClick={sendAuthRequest}
                                >
                                  {labels.send_authorization}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobLogs;
